/**
 * Where the permission link in the email lands (`/consent#f=..&p=..&t=..`).
 *
 * The parent is signed in by the time Home renders -- a link opened while
 * signed out survives the sign-in redirect in sessionStorage
 * (family/consent.ts `takeConsentLink`). This confirms it once, says what
 * happened, and forgets the token either way.
 */

import { useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { Layer } from '../brand/Layer';
import { confirmConsent, forgetConsentLink, takeConsentLink } from '../family/consent';
import type { Person } from '../family/state';

type Outcome = { kind: 'done'; name: string } | { kind: 'other-family' } | { kind: 'failed'; message: string };

export function ConsentLanding({
  familyId,
  people,
  onChanged,
}: {
  familyId: string;
  people: Person[];
  onChanged: (work: () => Promise<void>) => void;
}) {
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    const link = takeConsentLink();
    if (!link) return;
    if (link.familyId !== familyId) {
      // Leave it held: the parent may switch to the family it belongs to.
      setOutcome({ kind: 'other-family' });
      return;
    }
    forgetConsentLink();
    const name = people.find((person) => person.id === link.personId)?.name || 'your child';
    void confirmConsent(link.familyId, link.personId, link.token)
      .then(() => {
        setOutcome({ kind: 'done', name });
        onChanged(async () => {});
      })
      .catch((problem: unknown) => {
        setOutcome({
          kind: 'failed',
          message: problem instanceof ApiError ? problem.message : 'That permission link could not be used.',
        });
      });
    // Once per family opened; `people` changing must not resend.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId]);

  if (!outcome) return null;
  const close = () => setOutcome(null);
  return (
    <Layer onClose={close}>
      <div className="dialog-layer" onClick={(event) => event.target === event.currentTarget && close()}>
        <div className="dialog" role="alertdialog" aria-modal="true" aria-labelledby="consent-landing">
          {outcome.kind === 'done' ? (
            <>
              <h3 id="consent-landing">Permission given</h3>
              <p>
                {outcome.name} can now use their own page. You can see what we hold about them, or
                withdraw this, in Settings, Privacy.
              </p>
            </>
          ) : outcome.kind === 'other-family' ? (
            <>
              <h3 id="consent-landing">That link is for another family</h3>
              <p>Switch to the family it was sent for, and it will finish there.</p>
            </>
          ) : (
            <>
              <h3 id="consent-landing">That link did not work</h3>
              <p>{outcome.message}</p>
            </>
          )}
          <div className="actions">
            <button type="button" className="primary" onClick={close} data-autofocus>OK</button>
          </div>
        </div>
      </div>
    </Layer>
  );
}
